import React from 'react';
import type {
  AdminCompanyDto,
  AdminLicenseDto,
  AdminMutationOutcome,
  AdminUserCreateRequest,
  AdminUserDto,
  AdminUserPatchRequest,
  AdminUserStatus,
} from '../types';
import {
  useUserDirtyClose,
  useUserDraftSession,
  useUserMutations,
  type UserDraftSession,
} from '../hooks/useUserEditor';
import { hasDeliverableUserEmail, isProtectedSuperUser } from '../lib/adminUsers';
import { UserForm } from './UserDrawerFields';
import {
  AdminButton,
  ConfirmDialog,
  EntityDrawer,
  StatusPill,
  useAdminToast,
} from './components';
import './styles/adminUsers.css';

export interface UserDrawerProps {
  open: boolean;
  mode: 'invite' | 'edit';
  user: AdminUserDto | null;
  users: readonly AdminUserDto[];
  companies: readonly AdminCompanyDto[];
  licenses: readonly AdminLicenseDto[];
  loading?: boolean;
  now?: Date;
  onClose: () => void;
  onCreate: (request: AdminUserCreateRequest) => Promise<AdminUserDto>;
  onSave: (id: number, patch: AdminUserPatchRequest) => Promise<AdminUserDto>;
  onDelete: (id: number) => Promise<AdminMutationOutcome>;
  onResetPassword: (id: number) => Promise<unknown>;
  onChangeStatus: (id: number, status: AdminUserStatus) => Promise<AdminUserDto>;
}

const titleCase = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const DrawerFooter: React.FC<{
  mode: UserDrawerProps['mode'];
  protectedSuper: boolean;
  protectionNoteId: string;
  canSubmit: boolean;
  submitting: boolean;
  cancel: () => Promise<void>;
  deleteUser: () => void;
  submit: () => Promise<void>;
}> = (props) => (
  <>
    {props.mode === 'edit' && (
      <AdminButton
        variant="danger"
        disabled={props.submitting}
        aria-disabled={props.protectedSuper || props.submitting}
        aria-describedby={props.protectedSuper ? props.protectionNoteId : undefined}
        onClick={() => {
          if (!props.protectedSuper && !props.submitting) {
            props.deleteUser();
          }
        }}
      >
        Delete user
      </AdminButton>
    )}
    <span className="admin-user-drawer-footer-spacer" />
    <AdminButton variant="ghost" disabled={props.submitting} onClick={() => void props.cancel()}>Cancel</AdminButton>
    <AdminButton variant="primary" disabled={!props.canSubmit} onClick={() => void props.submit()}>
      {props.submitting ? 'Saving…' : props.mode === 'invite' ? 'Invite user' : 'Save changes'}
    </AdminButton>
  </>
);

const DrawerActions: React.FC<{
  user: AdminUserDto;
  dirty: boolean;
  submitting: boolean;
  protectedSuper: boolean;
  emailNoteId: string;
  protectionNoteId: string;
  dirtyStatusNoteId: string;
  reset: () => Promise<void>;
  setStatus: (status: AdminUserStatus) => Promise<void>;
}> = (props) => {
  const { showToast } = useAdminToast();
  const canCopy = Boolean(props.user.email?.trim());
  const canDeliver = hasDeliverableUserEmail(props.user.email);
  const copyEmail = async () => {
    if (!canCopy || !props.user.email || !navigator.clipboard) {
      showToast('The email address could not be copied.', { tone: 'critical' });
      return;
    }
    try {
      await navigator.clipboard.writeText(props.user.email);
      showToast('Email copied.');
    } catch {
      showToast('The email address could not be copied.', { tone: 'critical' });
    }
  };
  const nextStatus = props.user.status === 'blocked' ? 'active' : 'blocked';
  const statusBlocked = props.protectedSuper || props.dirty;
  const statusNoteId = props.protectedSuper
    ? props.protectionNoteId
    : props.dirty ? props.dirtyStatusNoteId : undefined;
  return (
    <>
      <AdminButton
        disabled={props.submitting}
        aria-disabled={!canCopy || props.submitting}
        aria-describedby={!canCopy ? props.emailNoteId : undefined}
        onClick={() => {
          if (canCopy && !props.submitting) void copyEmail();
        }}
      >Copy email</AdminButton>
      <AdminButton
        disabled={props.submitting}
        aria-disabled={!canDeliver || props.submitting}
        aria-describedby={!canDeliver ? props.emailNoteId : undefined}
        onClick={() => {
          if (canDeliver && !props.submitting) void props.reset();
        }}
      >Reset password</AdminButton>
      <AdminButton
        disabled={props.submitting}
        aria-disabled={statusBlocked || props.submitting}
        aria-describedby={statusNoteId}
        onClick={() => {
          if (!statusBlocked && !props.submitting) void props.setStatus(nextStatus);
        }}
      >
        {nextStatus === 'blocked' ? 'Block user' : 'Unblock user'}
      </AdminButton>
    </>
  );
};

const DrawerBody: React.FC<{
  loading: boolean;
  mode: UserDrawerProps['mode'];
  user: AdminUserDto | null;
  companies: readonly AdminCompanyDto[];
  licenses: readonly AdminLicenseDto[];
  now: Date;
  canCopy: boolean;
  canDeliver: boolean;
  protectedSuper: boolean;
  submitting: boolean;
  emailNoteId: string;
  protectionNoteId: string;
  dirtyStatusNoteId: string;
  session: UserDraftSession;
}> = (props) => {
  if (props.loading || (props.mode === 'edit' && !props.user)) {
    return <div className="admin-user-drawer-loading" role="status">Loading user details...</div>;
  }
  const visibleValidation = props.session.dirty ? props.session.validation : {};
  const errors = { ...visibleValidation, ...props.session.fieldErrors };
  return (
    <>
      {props.session.saveError && <div className="admin-user-form-error" role="alert">{props.session.saveError}</div>}
      {props.session.assignmentNotice && <div className="admin-user-assignment-notice" role="status">{props.session.assignmentNotice}</div>}
      {(!props.canCopy || !props.canDeliver) && props.mode === 'edit' && (
        <div id={props.emailNoteId} className="admin-user-action-note">
          {props.canCopy
            ? 'Password reset needs a valid email address.'
            : 'Copy and password reset need a valid email address.'}
        </div>
      )}
      {props.session.dirty && props.mode === 'edit' && (
        <div id={props.dirtyStatusNoteId} className="admin-user-action-note">
          Save or discard draft changes before changing user status.
        </div>
      )}
      {props.protectedSuper && (
        <div id={props.protectionNoteId} className="admin-user-protection-note">
          The last active super administrator account cannot be blocked, demoted, or deleted.
        </div>
      )}
      <UserForm
        mode={props.mode}
        user={props.user}
        companies={props.companies}
        licenses={props.licenses}
        now={props.now}
        draft={props.session.draft}
        errors={errors}
        protectedSuper={props.protectedSuper}
        disabled={props.submitting}
        setField={props.session.setField}
        changeCompany={props.session.changeCompany}
        toggleLicense={props.session.toggleLicense}
      />
    </>
  );
};

const useUserDrawerNoteIds = () => {
  const baseId = React.useId();
  return {
    dirtyStatus: `${baseId}-dirty-status`,
    email: `${baseId}-email`,
    protection: `${baseId}-protection`,
  };
};

const UserDrawer: React.FC<UserDrawerProps> = (props) => {
  const loading = props.loading ?? false;
  const protectedSuper = props.user ? isProtectedSuperUser(props.user, props.users) : false;
  const now = props.now ?? new Date();
  const session = useUserDraftSession(props.open, props.mode, props.user, props.licenses,
    props.companies[0]?.id ?? 0);
  const mutations = useUserMutations({ ...props, loading, session });
  const dirtyClose = useUserDirtyClose(session.dirty, mutations.deleteOpen, props.onClose);
  const canCopy = Boolean(props.user?.email?.trim());
  const canDeliver = hasDeliverableUserEmail(props.user?.email ?? null);
  const title = props.mode === 'invite' ? 'New user' : props.user?.username ?? 'Loading user...';
  const noteIds = useUserDrawerNoteIds();

  return (
    <>
      <EntityDrawer
        open={props.open}
        title={title}
        ariaLabel={title}
        eyebrow={(
          <div className="admin-user-drawer-eyebrow">
            <StatusPill tone={session.draft.status === 'active' ? 'success' : session.draft.status === 'blocked' ? 'critical' : 'info'}>
              {titleCase(session.draft.status)}
            </StatusPill>
            <StatusPill tone="line">{session.draft.role}</StatusPill>
          </div>
        )}
        actions={props.mode === 'edit' && props.user && !loading ? (
          <DrawerActions
            user={props.user}
            dirty={session.dirty}
            submitting={mutations.submitting}
            protectedSuper={protectedSuper}
            emailNoteId={noteIds.email}
            protectionNoteId={noteIds.protection}
            dirtyStatusNoteId={noteIds.dirtyStatus}
            reset={mutations.resetPassword}
            setStatus={mutations.setStatus}
          />
        ) : undefined}
        footer={(
          <DrawerFooter
            mode={props.mode}
            protectedSuper={protectedSuper}
            protectionNoteId={noteIds.protection}
            canSubmit={mutations.canSubmit}
            submitting={mutations.submitting}
            cancel={dirtyClose.requestCancel}
            deleteUser={() => mutations.setDeleteOpen(true)}
            submit={mutations.submit}
          />
        )}
        dirty={session.dirty}
        onDirtyClose={dirtyClose.confirmDirtyClose}
        onClose={props.onClose}
      >
        <DrawerBody
          loading={loading}
          mode={props.mode}
          user={props.user}
          companies={props.companies}
          licenses={props.licenses}
          now={now}
          canCopy={canCopy}
          canDeliver={canDeliver}
          protectedSuper={protectedSuper}
          submitting={mutations.submitting}
          emailNoteId={noteIds.email}
          protectionNoteId={noteIds.protection}
          dirtyStatusNoteId={noteIds.dirtyStatus}
          session={session}
        />
      </EntityDrawer>
      <ConfirmDialog
        open={mutations.deleteOpen}
        title={`Delete ${props.user?.username ?? 'this user'}?`}
        description={`${props.user?.username ?? 'This user'} (${props.user?.email ?? 'no valid email'}) will lose account access to PipeEX. ${props.user?.licenseIds.length ?? 0} license assignments, credentials, sessions, and exclusive registrations will be removed. Company licenses remain intact.`}
        confirmLabel={mutations.submitting ? 'Deleting…' : 'Delete user'}
        confirmDisabled={mutations.submitting}
        closeOnConfirm={false}
        onConfirm={mutations.confirmDelete}
        onClose={() => mutations.setDeleteOpen(false)}
      />
      <ConfirmDialog
        open={dirtyClose.discardOpen}
        title="Discard unsaved changes?"
        description="Your user edits and draft license assignments will be lost."
        confirmLabel="Discard changes"
        closeOnConfirm={false}
        onConfirm={() => dirtyClose.resolveDiscard(true)}
        onClose={() => dirtyClose.resolveDiscard(false)}
      />
    </>
  );
};

export default UserDrawer;
